/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { Button, Stack, Paper, Typography, Divider, Grid, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Formik } from 'formik';
import moment from "moment";
import SaveIcon from '@mui/icons-material/Save';
import get from "lodash/get";
import map from "lodash/map";
import isEmpty from "lodash/isEmpty";

import Layout from "../../../components/layout/Layout";
import InputText from "../../../components/form/InputText";
import InputTextDetail from "../../../components/form/InputTextDetail";
import Autocomplete from "../../../components/form/AutoComplete";
import DatePickerUi from "../../../components/form/DatePickerUi";
import Notification from "../../../components/form/Notification";
import Alert from "../../../components/form/Alert";

import validator from "./validator";
import { useCanView } from "../../../config/hook/UserPermissions";

import { postInvoiceClient }      from "../../../store/transactions/thunk/invoiceClient/post";
import { postInvoiceClientReset } from "../../../store/transactions/actions/invoiceClient/post";
import { getClient }              from "../../../store/masters/thunk/client/getAll";

const NewEdit = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [__] = useTranslation("invoiceClient");
    const canView = useCanView();
    const [showNoti, setShowNoti] = useState({ open: false, msg: "", variant: "error", action: "post" });
    const [alert, setAlert]       = useState({ open: false, title: "", subtitle: "", type: "" });

    const userData = JSON.parse(localStorage.getItem('userData'));

    const invoiceType = map(userData?.invoicesTypes, ({ tipoId, description }) => ({
        value: tipoId,
        label: description,
    }));

    const divisions = map(userData?.divisions, ({ divisionId, divisionName }) => ({
        value: divisionId,
        label: divisionName,
    }));

    const post   = useSelector((state) => state.transactions.invoiceClient.post);
    const client = useSelector((state) => state.masters.client);

    const clients = map(
        get(client, "data.data", []).filter((c) => get(c, "isActive") === "ACTIVO"),
        ({ customerId, name }) => ({ value: customerId, label: `${customerId} - ${name}` }),
    );

    const closeAlert = () => {
        setAlert({ open: false, title: "", subtitle: "", type: "", btn: "" });
    };

    const setError = (err) => {
        if (!isEmpty(err) && !!get(err, "Message", "")) {
            setAlert({
                open: true,
                title: get(err, "Message", ""),
                subtitle: (<ul>{map(get(err, "ValidationError", []), (item) => <li>{`• ${item}`}</li>)}</ul>),
                type: "error",
                func: closeAlert
            });
        } else {
            setAlert({
                open: true,
                type: "error",
                title: "Uno o más errores de validación han ocurrido.",
                subtitle: (<li>{get(err, "ErrorMessage")}</li>),
                func: closeAlert
            });
        }
    };

    useEffect(() => {
        dispatch(postInvoiceClientReset());
        dispatch(getClient({ Export: true }));
    }, []);

    useEffect(() => {
        if (get(post, "isSuccess", false)) {
            setShowNoti({ open: true, msg: "", variant: "success", action: "post" });
            navigate(`/transactions/invoice-client/${get(post, "id") || get(post, "data.id")}`);
        }
        if (get(post, "isReject", false)) { setError(get(post, "error", {})); }
    }, [post]);

    const onSubmit = (values) => {
        if (!canView("TRANSACCIONES/INVOICES", "add")) {
            setAlert({
                open: true,
                title: "Permiso Denegado.",
                subtitle: (<ul><li>• No tienes permiso para crear un invoice.</li></ul>),
                type: "error",
                func: closeAlert,
            });
            return;
        }
        const body = {
            customerId:  get(values, "idClient"),
            fecha:       moment(get(values, "date")).format(),
            divisionId:  get(values, "division"),
            tipoInvoice: get(values, "invoiceType"),
            anombreDe:   get(values, "aNameOf"),
            comentarios: get(values, "comments"),
        };
        dispatch(postInvoiceClient(body));
    };

    const initialValues = {
        idClient:    "",
        date:        new Date(),
        division:    "",
        invoiceType: "",
        aNameOf:     "",
        comments:    "",
    };

    return (
        <Layout
            propsToolbar={{
                title: __(`head.new.name`),
                srute: __(`head.new.code`),
            }}
        >
            <Formik initialValues={initialValues} validationSchema={validator(__)} onSubmit={onSubmit}>
                {formik => (
                    <form onSubmit={get(formik, "handleSubmit")}>
                        <Paper className='p-4'>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <LoadingButton
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<SaveIcon />}
                                    type="submit"
                                    loading={get(post, "isLoading")}
                                >
                                    {__(`action.save`)}
                                </LoadingButton>
                                <Button
                                    variant='outlined'
                                    color="secondary"
                                    onClick={() => navigate("/transactions/invoice-client")}
                                    disabled={get(post, "isLoading")}
                                >
                                    {__(`action.cancel`)}
                                </Button>
                            </Stack>
                        </Paper>

                        <Paper className='mt-4'>
                            <Stack className='p-4' direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                                <Typography component="h4" variant="h4">{__(`labels.1`)}</Typography>
                            </Stack>
                            <Divider />
                            <Box className='p-4'>
                                <Grid container spacing={{ xs: 2, md: 3 }}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Autocomplete
                                            name="idClient"
                                            formik={formik}
                                            label={__(`form.idClient.label`)}
                                            placeholder={__(`form.idClient.placeholder`)}
                                            options={clients}
                                            disabled={get(post, "isLoading")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Autocomplete
                                            name="division"
                                            formik={formik}
                                            label={__(`form.division.label`)}
                                            placeholder={__(`form.division.placeholder`)}
                                            options={divisions}
                                            disabled={get(post, "isLoading")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Autocomplete
                                            name="invoiceType"
                                            formik={formik}
                                            label={__(`form.invoiceType.label`)}
                                            placeholder={__(`form.invoiceType.placeholder`)}
                                            options={invoiceType}
                                            disabled={get(post, "isLoading")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <DatePickerUi
                                            formik={formik}
                                            name="date"
                                            label={__(`form.date.label`)}
                                            placeholder={__(`form.date.placeholder`)}
                                            disabled={get(post, "isLoading")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <InputText
                                            formik={formik}
                                            name="aNameOf"
                                            label={__(`form.aNameOf.label`)}
                                            placeholder={__(`form.aNameOf.placeholder`)}
                                            disabled={get(post, "isLoading")}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <InputText
                                            formik={formik}
                                            name="comments"
                                            label={__(`form.comments.label`)}
                                            placeholder={__(`form.comments.placeholder`)}
                                            disabled={get(post, "isLoading")}
                                            multiline
                                            rows={3}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </form>
                )}
            </Formik>
            <Notification __={__} showNoti={showNoti} setShowNoti={setShowNoti} />
            <Alert
                title={get(alert, "title")}
                subtitle={get(alert, "subtitle")}
                btn1={{ func: get(alert, "func") }}
                btn2={{}}
                type={get(alert, "type")}
                openAlert={get(alert, "open")}
                closeAlert={closeAlert}
            />
        </Layout>
    );
};

export default NewEdit;
